import './index.scss';
import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {Box, Skeleton, Typography} from "@mui/material";
import axios from "axios";
import StaticDashboardWidgetGenerator from "../../../analytics/dashboard-v2/static-widgets";
import Grid2 from "@mui/material/Unstable_Grid2";
import DashboardWidget from "../../../analytics/dashboard-v2/components/dashboard-widget";
import useQuery from "../../../../../../hooks/use-query";
import {DateRangeButton} from "../../../../../../components/app-specific/date-range-controller";
import useStaticStatsWidgetsState, {
    STATIC_STATS_SAVED_STATE_KEYS,
} from "../../../analytics/dashboard-v2/hooks/use-static-stats-widgets-state";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {NumberVisibilityModeProvider} from "../../../analytics/dashboard-v2/hooks/use-number-visibility-mode";
import NumberVisibilityModeToggle from "../../../analytics/dashboard-v2/components/number-visibility-mode-toggle";

const ProductInformationStatsView = ({partNo, loading: loadingProps}) => {
    const [loadingStats, setLoadingStats] = useState(true);
    const [_widgets, setWidgets] = useState([]);
    const isMounted = useIsMounted();
    const operatorAccessLevels = useAccessLevels();
    const canExportWidgets = Boolean(operatorAccessLevels.packages.dashboard.exportDashboardWidgets);

    const {
        loadingConfig,
        savingWidgetConfig,
        augmentCategorizedWidgets,
        saveWidgetOrderBy,
        saveWidgetConfig,
    } = useStaticStatsWidgetsState(STATIC_STATS_SAVED_STATE_KEYS.product);

    const widgets = useMemo(() => augmentCategorizedWidgets(_widgets), [_widgets, augmentCategorizedWidgets]);

    const loading = useMemo(() => loadingProps || loadingStats || loadingConfig, [loadingProps, loadingStats, loadingConfig])

    /**
     * Fetches the product information widgets from the server.
     */
    const getProductInformationWidgets = useCallback(async (cancelToken) => {
        setLoadingStats(true);
        const widgets = await StaticDashboardWidgetGenerator.productInformationView(cancelToken);
        if (!isMounted())
            return;
        setWidgets(widgets ?? []);
        if (!cancelToken?.reason)
            setLoadingStats(false);
    }, [isMounted]);

    /**
     * As soon as the component is mounted:
     * - Fetches the widgets.
     */
    useEffect(() => {
        const cancelTokenSource = axios.CancelToken.source();
        void getProductInformationWidgets(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [getProductInformationWidgets])

    const [query, setQuery] = useQuery();
    const filters = useMemo(() => ({
        ...query,
        partNumbers: partNo ? [JSON.stringify({partNo: partNo})] : [],
    }), [partNo, query]);

    /**
     * Defaults the date range in the query when missing.
     */
    useEffect(() => {
        setQuery((prev) => ({
            ...prev,
            ...(!('dateRangeValue' in prev) && {
                dateRangeValue: {
                    mode: 'auto',
                    auto: {type: 'current', batch: 'year', count: 1, untilNow: true},
                },
            }),
        }), {replace: true})
    }, [setQuery])

    return (
        <>
            <Grid2 container spacing={2} sx={{width: '100%'}}>
                <Grid2 xs={12}>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1}}>
                        <NumberVisibilityModeToggle/>
                        <DateRangeButton
                            disallowedTypes={['next']}
                            disallowedBatches={['hour']}
                            disallowFromNow
                            value={filters.dateRangeValue}
                            onCommitted={(value) => {
                                setQuery((prevState) => ({
                                    ...prevState,
                                    dateRangeValue: value,
                                }), {replace: true})
                            }}
                        />
                    </Box>
                </Grid2>
                {
                    loading &&
                    Array.from({length: 8}).map((_, index) => (
                        <Grid2 xs={12} lg={6} key={index}>
                            <Skeleton height={350} sx={{transform: 'none'}}/>
                        </Grid2>
                    ))
                }
                {
                    !loading &&
                    widgets.map((group) => (
                        <React.Fragment key={group.category}>
                            <Grid2 xs={12}>
                                <Typography variant={'subtitle1'} fontWeight={600} color={'text.secondary'} gutterBottom>
                                    {group.category}
                                </Typography>
                            </Grid2>
                            {
                                group.widgets.map((widget) => (
                                    <Grid2 {...widget?.size} key={widget.connectionId || widget.id}>
                                        <DashboardWidget
                                            simplified
                                            widget={widget}
                                            filters={filters}
                                            canViewWidgetFilters={false}
                                            canManageWidgetFilters={false}
                                            canExportWidgets={canExportWidgets}
                                            savingDashboardWidgetConfig={savingWidgetConfig}
                                            saveDashboardWidgetConfig={saveWidgetConfig}
                                            saveWidgetOrderBy={saveWidgetOrderBy}
                                        />
                                    </Grid2>
                                ))
                            }
                        </React.Fragment>
                    ))
                }
            </Grid2>
        </>
    );
}

const ProductInformationStatsViewWithNumberVisibility = (props) => (
    <NumberVisibilityModeProvider>
        <ProductInformationStatsView {...props} />
    </NumberVisibilityModeProvider>
);

export default ProductInformationStatsViewWithNumberVisibility;
